<template>
  <div class="flex flex-col gap-1">
    <span class="text-xs font-medium text-text-default">Task type</span>

    <div class="flex flex-col gap-2" role="radiogroup" aria-label="Task type">
      <div
        v-for="type in visible_types"
        :key="type.value"
        role="radio"
        :aria-checked="modelValue === type.value ? 'true' : 'false'"
        :tabindex="modelValue === type.value ? 0 : -1"
        class="flex flex-col gap-0.5 px-3 py-2 rounded-md border"
        :class="cardClass(type)"
        @click="select(type)"
        @keydown.enter.prevent="select(type)"
        @keydown.space.prevent="select(type)"
      >
        <span
          class="text-sm font-medium"
          :class="modelValue === type.value ? 'text-text-link' : 'text-text-default'"
        >{{ type.label }}</span>
        <span class="text-xs text-text-subtle">{{ type.description }}</span>

        <!-- Said on the card rather than after the click. A type that cannot be created here is a fact
             about the form, and learning it only on selection makes the card a dead end the person has to
             back out of. -->
        <span v-if="type.unavailable" class="text-xs text-text-subtle italic">{{ type.unavailable }}</span>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * The three task types the design doc's self-serve manager offers: Recap, Watcher, Event trigger.
 *
 * WHY THESE THREE AND NOT THE ENUM'S THREE. `ScheduledTaskType` is {ScheduledQuestion, Watcher,
 * MorningBrief}, which is not the same list and deliberately so:
 *
 *  - Recap IS ScheduledQuestion. One name for a dealership user, another for the executor.
 *  - Watcher is Watcher.
 *  - Event trigger is NOT a type at all — an event-driven task is a ScheduledQuestion carrying entries in
 *    `TaskTrigger.Events`. Adding an enum member for it would have meant a Cosmos migration for something
 *    the model already expresses.
 *  - MorningBrief is a real type and is deliberately ABSENT. A brief's content comes from the fact
 *    catalogue rather than from anything a person types here, so there is no form for it to open:
 *    the only decisions are whether to have one and what time it lands. Both live on
 *    MorningBriefOptIn, its own card above the create form. (This note used to end "so it is
 *    provisioned, not self-served" — the reasoning still holds, the conclusion no longer does.)
 *    ScheduledEdit no longer lists the type either: the brief is one per person, and that card is the
 *    only place it is turned on, off, or rescheduled.
 *
 * Kept in `computed` rather than `data()` so the list re-renders when availability changes — the same
 * reason the localisation conventions require it of any translated option list.
 */
export default {
  name: 'ScheduledTypeCards',
  props: {
    modelValue: {
      type: String,
      required: false,
      default: 'Recap',
    },
    /**
     * Whether a watcher can be authored from this form.
     *
     * `ScheduledCreate` passes true — the natural-language compile route exists
     * (`WatcherDraftCompiler` behind `POST api/scheduled/compile-watcher`, with restate-and-confirm),
     * so the card is live. An earlier version of this comment said "false until that route exists",
     * which stopped being true when the route was built and left the component reading as though
     * watchers were switched off.
     *
     * The default stays false because a watcher saved without a compiled `WatcherCondition` can never
     * fire, and a caller that has not thought about this should get the silent-safe answer rather than
     * a form whose output would be permanently quiet. It is a floor for a careless mount, not a
     * statement that the feature is unfinished.
     */
    watcherAvailable: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Whether an event trigger can be authored. Unlike the watcher this is not a build-state question —
     * the ingestion pipeline is live — but the card still needs a way to stand down if a deployment
     * turns out to have no event source feeding `EventsController`.
     */
    eventsAvailable: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * NPM-237: whether this environment lets watchers and event triggers be created at all — the
     * availability endpoint's `watchersEnabled` (FUSION_SCHEDULED_TASKS and FUSION_SCHEDULED_WATCHERS
     * both on). False hides both cards rather than greying them out: the person cannot do anything
     * about an environment switch, so there is nothing to explain beside a card they cannot use. The
     * two props above still decide availability per store once the cards are shown.
     */
    watchersEnabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  computed: {
    all_types() {
      return [
        {
          value: 'Recap',
          label: 'Recap',
          description: 'Answers a saved question on a timer',
        },
        {
          value: 'Watcher',
          label: 'Watcher',
          description: 'Speaks only when a line is crossed',
          hidden: !this.watchersEnabled,
          unavailable: this.watcherAvailable
            ? ''
            : 'Watchers are temporarily unavailable.',
        },
        {
          value: 'EventTrigger',
          label: 'Event trigger',
          description: 'Reacts to something happening',
          hidden: !this.watchersEnabled,
          // Event triggers are set up in the systems that raise the events, not from this form, so the card
          // is always disabled: shown greyed and unselectable, with no note under it. `eventsAvailable` is
          // not read while this holds; the expression it fed was:
          //   this.eventsAvailable ? '' : 'Event triggers are unavailable for your store right now.'
          disabled: true,
          unavailable: '',
        },
      ];
    },
    /**
     * All three cards are offered.
     *
     * CORRECTION. Event trigger was withheld on the belief that nothing ingested events — that
     * `TaskTrigger.Events` was validated on write and then ignored. That was wrong. The ingestion side
     * is built and running: `CosmosEventIngestionQueue`, `EventQueueDrainer` registered as a hosted
     * service, `EventsController`, a seven-type `EventTypeRegistry`, and
     * `ScheduledTaskRepository.GetCandidatesForEventAsync`, which matches stored tasks on exactly this
     * field. The misreading came from `TaskTrigger.Events`' own doc comment, which says the "Phase-1
     * dispatcher ignores these" — true and misleading, because the drainer consumes them, not the task
     * dispatcher, and that comment predates the drainer.
     *
     * A card is still withheld when it genuinely cannot deliver — see the Watcher card's
     * `unavailable`, and `push_available` in ScheduledCreate. The rule stands; only the fact changed.
     */
    visible_types() {
      return this.all_types.filter(t => !t.hidden);
    },
  },
  methods: {
    select(type) {
      // A type whose form cannot produce a working task is not selectable. The card still renders, and
      // still explains itself, because the person needs to know the capability exists and where it lives.
      if (type.unavailable) return;
      // A disabled card is unselectable like an unavailable one, but carries no note.
      if (type.disabled) return;
      if (type.value === this.modelValue) return;
      this.$emit('update:modelValue', type.value);
    },
    cardClass(type) {
      if (type.unavailable || type.disabled) return 'border-border-separator bg-surface-2 opacity-60 cursor-not-allowed';
      if (this.modelValue === type.value) return 'border-brand-outline bg-brand-subtle/30 cursor-pointer';
      return 'border-border-separator bg-surface-1 cursor-pointer hover:bg-surface-2';
    },
  },
};
</script>
